import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NavBar from '../components/navbar';
import apiClient from '../services/apiClient';
import { ACCOUNT_TYPE_SEATS, ADMIN_LIMITS } from '../constants';
import { Button } from 'react-bootstrap';
import { FaUser, FaSort, FaSortUp, FaSortDown, FaTrash, FaUserFriends, FaUserSecret, FaArrowRight, FaPause, FaPlay, FaPlus } from 'react-icons/fa';


function Avatar({ name, lastName, email, size = 36 }) {
  const initials = name
    ? `${name.charAt(0)}${lastName ? lastName.charAt(0) : ''}`.toUpperCase()
    : email.charAt(0).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg, #1565c0, #42a5f5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.38,
      flexShrink: 0, userSelect: 'none',
    }}>
      {initials}
    </div>
  );
}

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <FaSort style={{ opacity: 0.35, marginLeft: 4, fontSize: 11 }} />;
  return sortDir === 'asc'
    ? <FaSortUp style={{ marginLeft: 4, fontSize: 11, color: '#1565c0' }} />
    : <FaSortDown style={{ marginLeft: 4, fontSize: 11, color: '#1565c0' }} />;
}

const COLS = [
  { key: 'name', label: 'User' },
  { key: 'email', label: 'Email' },
  { key: 'teams', label: 'Team' },
  { key: 'role', label: 'Role' },
  { key: 'status', label: 'Status' },
];

function roleLabel(user) {
  if (user.role === 'admin') return 'Admin';
  if (user.can_manage_teams) return 'Lead Agent';
  const roles = user.team_roles || [];
  if (roles.includes('team_lead')) return 'Team Lead';
  if (roles.includes('solo_agent') || roles.includes('single_agent')) return 'Solo Agent';
  return 'Member';
}

function statusBadge(status) {
  const map = {
    active:    { color: '#166534', label: 'Active' },
    invited:   { color: '#713f12', label: 'Invited' },
    inactive:  { color: '#6b7280', label: 'Inactive' },
    suspended: { color: '#92400e', label: 'Suspended' },
  };
  const s = map[status] || map.active;
  return (
    <span style={{ color: s.color, fontSize: 12, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

const cancelBtnStyle = {
  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
  border: 'none',
  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
  transition: 'all 0.2s ease-in-out',
  padding: '8px 20px',
};

const primaryBtnStyle = {
  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
  transition: 'all 0.2s ease-in-out',
  padding: '8px 20px',
};

const purplePrimaryStyle = {
  background: '#7c3aed',
  borderColor: '#7c3aed',
  color: '#fff',
  ...primaryBtnStyle,
};

export default function Users({ onProfileClick, onBillingClick, onSeatsUpdate }) {
  const { user, isOwner, canManageUsersTeams, hasDelegatedWorkspace, startImpersonation } = useAuth();
  const navigate = useNavigate();

  // A user may own their own workspace AND separately hold delegated Admin access
  // to a DIFFERENT owner's workspace (admin_for_owner_*). This page always defaults
  // to the user's own workspace — it must never silently switch away from it — and
  // only shows the delegated workspace when the user explicitly toggles to it.
  const [viewingDelegatedWorkspace, setViewingDelegatedWorkspace] = useState(false);
  const isViewingDelegated = hasDelegatedWorkspace && viewingDelegatedWorkspace;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const [billingSeats, setBillingSeats] = useState(null);
  const [confirmUser, setConfirmUser] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);
  const [removeStep, setRemoveStep] = useState('checking'); // 'checking'|'ready'|'need_transfer'|'blocked'|'executing'
  const [removeCheckResult, setRemoveCheckResult] = useState(null);
  const [removeAfterTransfer, setRemoveAfterTransfer] = useState(false);

  // Suspend state
  const [suspendUser, setSuspendUser] = useState(null);
  const [suspending, setSuspending] = useState(false);
  const [suspendError, setSuspendError] = useState(null);
  const [suspendStep, setSuspendStep] = useState('checking'); // 'checking'|'ready'|'need_transfer'|'blocked'|'executing'
  const [suspendCheckResult, setSuspendCheckResult] = useState(null);
  const [suspendAfterTransfer, setSuspendAfterTransfer] = useState(false);

  // Reactivate state
  const [reactivateUser, setReactivateUser] = useState(null);
  const [reactivating, setReactivating] = useState(false);
  const [reactivateError, setReactivateError] = useState(null);

  const [confirmImpersonateUser, setConfirmImpersonateUser] = useState(null);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState(null);

  // Add User state
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserRole, setAddUserRole] = useState('member');
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addUserFirstName, setAddUserFirstName] = useState('');
  const [addUserLastName, setAddUserLastName] = useState('');
  const [addUserConnectionId, setAddUserConnectionId] = useState('');
  const [addUserPermissions, setAddUserPermissions] = useState([]);
  const [addUserConnections, setAddUserConnections] = useState([]);
  const [addUserConnectionsLoading, setAddUserConnectionsLoading] = useState(false);
  const [addUserConnectionsError, setAddUserConnectionsError] = useState(null);
  const [addUserSubmitting, setAddUserSubmitting] = useState(false);
  const [addUserError, setAddUserError] = useState(null);
  const [addUserAttempted, setAddUserAttempted] = useState(false);
  const [addUserTouched, setAddUserTouched] = useState({});

  // Transfer state
  const [transferUser, setTransferUser] = useState(null);
  const [transferStep, setTransferStep] = useState('select');
  const [transferTargets, setTransferTargets] = useState([]);
  const [transferTargetsLoading, setTransferTargetsLoading] = useState(false);
  const [transferTargetsError, setTransferTargetsError] = useState(null);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [transferConflicts, setTransferConflicts] = useState([]);
  const [conflictResolutions, setConflictResolutions] = useState({});
  const [taskResolutions, setTaskResolutions] = useState({});
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState(null);
  const [transferSuccessMsg, setTransferSuccessMsg] = useState(null);

  useEffect(() => {
    if (!isOwner && !canManageUsersTeams) {
      navigate('/tasks', { replace: true });
    }
  }, [isOwner, canManageUsersTeams, navigate]);

  const loadUsers = useCallback(async () => {
    if (!user?.email) return;
    setLoading(true);
    setError(null);
    try {
      const ownerEmail = isViewingDelegated
        ? user.admin_for_owner_email
        : (user.role === 'admin' ? user.owner_email : user.email);
      const res = await apiClient.get('/api/users/workspace', {
        params: { ownerEmail },
      });
      if (res.data.success) {
        setUsers(res.data.users);
      } else {
        setError(res.data.message || 'Failed to load users');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [user?.email, user?.role, user?.owner_email, user?.admin_for_owner_email, isViewingDelegated]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadBillingSeats = useCallback(async () => {
    const userId = isViewingDelegated
      ? (user?.admin_for_owner_user_id || user?.id || user?.userId || user?.user_id)
      : (user?.role === 'admin'
        ? (user?.owner_user_id || user?.id || user?.userId || user?.user_id)
        : (user?.id || user?.userId || user?.user_id));
    if (!userId) return;
    try {
      const res = await apiClient.get(`/api/billing/${userId}`);
      if (res.data.success && res.data.data?.currentPlan?.seatCount) {
        setBillingSeats(res.data.data.currentPlan.seatCount);
      }
    } catch {
      // fall through to defaultSeatLimit
    }
  }, [user?.id, user?.userId, user?.user_id, user?.role, user?.owner_user_id, user?.admin_for_owner_user_id, isViewingDelegated]);

  useEffect(() => {
    loadBillingSeats();
  }, [loadBillingSeats]);

  const effectiveAccountType = isViewingDelegated
    ? (user?.admin_for_owner_account_type || '')
    : (user?.role === 'admin'
      ? (user?.owner_account_type || user?.accountType || '')
      : (user?.accountType || ''));
  const normalizedAccountTypeKey = effectiveAccountType.toLowerCase().replace(/ /g, '_');
  const defaultSeatLimit = ACCOUNT_TYPE_SEATS[normalizedAccountTypeKey] || 1;
  const seatLimit = billingSeats ?? defaultSeatLimit;
  const usedSeats = users.filter(u => !u.suspended && u.role !== 'admin').length;
  const availableSeats = Math.max(seatLimit - usedSeats, 0);
  const adminLimit = ADMIN_LIMITS[effectiveAccountType] ?? ADMIN_LIMITS[normalizedAccountTypeKey] ?? 0;
  const usedAdmins = users.filter(u => u.role === 'admin').length;

  const sortedUsers = useMemo(() => {
    const getValue = (u, col) => {
      if (col === 'name') return `${u.name} ${u.last_name}`.trim().toLowerCase();
      if (col === 'teams') return (u.teams || []).join(', ').toLowerCase();
      if (col === 'role') return roleLabel(u).toLowerCase();
      if (col === 'status') return (u.status || '').toLowerCase();
      return (u[col] || '').toString().toLowerCase();
    };
    return [...users].sort((a, b) => {
      const av = getValue(a, sortCol);
      const bv = getValue(b, sortCol);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [users, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const closeRemoveModal = () => {
    if (removing) return;
    setConfirmUser(null);
    setRemoveStep('checking');
    setRemoveCheckResult(null);
    setRemoveError(null);
    setRemoveAfterTransfer(false);
  };

  const closeSuspendModal = () => {
    if (suspending) return;
    setSuspendUser(null);
    setSuspendStep('checking');
    setSuspendCheckResult(null);
    setSuspendError(null);
    setSuspendAfterTransfer(false);
  };

  const handleSuspendExecute = async (targetUser) => {
    const memberToSuspend = targetUser || suspendUser;
    if (!memberToSuspend) return;
    setSuspending(true);
    setSuspendStep('executing');
    setSuspendError(null);
    try {
      const res = await apiClient.post('/api/users/suspend', {
        member_email: memberToSuspend.email,
      });
      if (res.data.success) {
        setUsers(prev => prev.map(u =>
          u.email === memberToSuspend.email
            ? { ...u, suspended: true, status: 'suspended' }
            : u
        ));
        closeSuspendModal();
      } else {
        setSuspendError(res.data.message || 'Failed to suspend user');
        setSuspendStep('ready');
      }
    } catch (err) {
      setSuspendError(err.response?.data?.message || err.message || 'Failed to suspend user');
      setSuspendStep('ready');
    } finally {
      setSuspending(false);
    }
  };

  const handleSuspendCheck = async (targetUser) => {
    const userToCheck = targetUser || suspendUser;
    if (!userToCheck) return;
    setSuspendStep('checking');
    setSuspendError(null);
    try {
      const res = await apiClient.post('/api/users/suspend/check', {
        member_email: userToCheck.email,
      });
      if (res.data.success) {
        setSuspendCheckResult(res.data);
        if (!res.data.can_suspend) {
          setSuspendStep('blocked');
        } else if (res.data.requires_transfer) {
          setSuspendStep('need_transfer');
        } else {
          setSuspendStep('ready');
        }
      } else {
        setSuspendError(res.data.message || 'Check failed');
        setSuspendStep('blocked');
      }
    } catch (err) {
      setSuspendError(err.response?.data?.message || err.message || 'Check failed');
      setSuspendStep('blocked');
    }
  };

  const handleReactivate = async () => {
    if (!reactivateUser) return;
    setReactivating(true);
    setReactivateError(null);
    try {
      const res = await apiClient.post('/api/users/reactivate', {
        member_email: reactivateUser.email,
      });
      if (res.data.success) {
        setUsers(prev => prev.map(u =>
          u.email === reactivateUser.email
            ? { ...u, suspended: false, status: 'active' }
            : u
        ));
        setReactivateUser(null);
        setReactivateError(null);
      } else {
        setReactivateError(res.data.message || 'Failed to reactivate user');
      }
    } catch (err) {
      setReactivateError(err.response?.data?.message || err.message || 'Failed to reactivate user');
    } finally {
      setReactivating(false);
    }
  };

  const handleRemoveExecute = async (targetUser) => {
    const memberToRemove = targetUser || confirmUser;
    if (!memberToRemove) return;
    setRemoving(true);
    setRemoveStep('executing');
    setRemoveError(null);
    try {
      const res = await apiClient.post('/api/users/remove/execute', {
        member_email: memberToRemove.email,
      });
      if (res.data.success) {
        setUsers(prev => prev.filter(u => u.email !== memberToRemove.email));
        setConfirmUser(null);
        setRemoveStep('checking');
        setRemoveCheckResult(null);
        setRemoveAfterTransfer(false);
        setTransferSuccessMsg(`${memberToRemove.name || memberToRemove.email} has been removed from your team.`);
        setTimeout(() => setTransferSuccessMsg(null), 5000);
      } else {
        setRemoveError(res.data.message || 'Failed to remove user');
        setRemoveStep('ready');
      }
    } catch (err) {
      setRemoveError(err.response?.data?.message || err.message || 'Failed to remove user');
      setRemoveStep('ready');
    } finally {
      setRemoving(false);
    }
  };

  const handleRemoveCheck = async (targetUser) => {
    const memberToCheck = targetUser || confirmUser;
    if (!memberToCheck) return;
    setRemoving(true);
    setRemoveStep('checking');
    setRemoveError(null);
    try {
      const res = await apiClient.post('/api/users/remove/check', {
        member_email: memberToCheck.email,
      });
      if (res.data.success) {
        const result = res.data;
        setRemoveCheckResult(result);
        const scope = result.delete_scope;
        if (scope === 'blocked_requires_new_lead_agent') {
          setRemoveStep('blocked');
        } else if (scope === 'transfer_required') {
          setRemoveStep('need_transfer');
        } else {
          setRemoveStep('ready');
        }
        setRemoving(false);
      } else {
        setRemoveError(res.data.message || 'Failed to check member status');
        setRemoveStep('blocked');
        setRemoving(false);
      }
    } catch (err) {
      setRemoveError(err.response?.data?.message || err.message || 'Failed to check member status');
      setRemoveStep('blocked');
      setRemoving(false);
    }
  };

  const handleImpersonateConfirmed = async () => {
    if (!confirmImpersonateUser) return;
    setImpersonating(true);
    setImpersonateError(null);
    try {
      const res = await apiClient.post('/api/users/impersonate', {
        targetEmail: confirmImpersonateUser.email,
      });
      if (res.data.success) {
        startImpersonation(res.data.user);
        setConfirmImpersonateUser(null);
        navigate('/tasks');
      } else {
        setImpersonateError(res.data.message || 'Impersonation failed');
      }
    } catch (err) {
      setImpersonateError(err.response?.data?.message || err.message || 'Impersonation failed');
    } finally {
      setImpersonating(false);
    }
  };

  const closeTransferModal = () => {
    if (transferring) return;
    setTransferUser(null);
    setTransferStep('select');
    setSelectedTransfer(null);
    setTransferConflicts([]);
    setConflictResolutions({});
    setTaskResolutions({});
    setTransferError(null);
    setTransferTargets([]);
  };

  const openTransferModal = async (u) => {
    setTransferUser(u);
    setTransferStep('select');
    setSelectedTransfer(null);
    setTransferConflicts([]);
    setConflictResolutions({});
    setTaskResolutions({});
    setTransferError(null);
    setTransferTargets([]);
    setTransferTargetsLoading(true);
    setTransferTargetsError(null);
    try {
      const res = await apiClient.get('/api/users/transfer-targets', {
        params: { originalEmail: u.email, ownerEmail: user.email },
      });
      if (res.data.success) {
        setTransferTargets(res.data.teams || []);
      } else {
        setTransferTargetsError(res.data.message || 'Failed to load eligible users');
      }
    } catch (err) {
      setTransferTargetsError(err.response?.data?.message || err.message || 'Failed to load eligible users');
    } finally {
      setTransferTargetsLoading(false);
    }
  };

  const handleTransferContinue = async () => {
    if (!selectedTransfer) return;
    setTransferStep('checking');
    setTransferError(null);
    try {
      const res = await apiClient.post('/api/users/transfer-check', {
        originalEmail: transferUser.email,
        targetEmail: selectedTransfer.targetUser.email,
        connectionId: selectedTransfer.connectionId,
        ownerEmail: user.email,
      });
      if (res.data.success) {
        if (!res.data.hasTransferableRecords) {
          setTransferStep('no-records');
        } else if (res.data.hasConflicts) {
          const defaults = {};
          (res.data.conflicts || []).forEach(c => {
            defaults[c.conflictKey] = 'keep_target';
          });
          setConflictResolutions(defaults);
          setTransferConflicts(res.data.conflicts || []);
          setTransferStep('conflict-warning');
        } else {
          setTransferStep('confirm');
        }
      } else {
        setTransferError(res.data.message || 'Conflict check failed');
        setTransferStep('select');
      }
    } catch (err) {
      setTransferError(err.response?.data?.message || err.message || 'Conflict check failed');
      setTransferStep('select');
    }
  };

  const handleTransferExecute = async () => {
    if (transferring) return;
    setTransferring(true);
    setTransferError(null);
    // Capture names before closeTransferModal clears state
    const origName = displayName(transferUser);
    const deactivatedCount = { current: 0 };
    try {
      const resolutionsList = Object.entries(conflictResolutions).map(
        ([conflictKey, choice]) => ({
          conflictKey,
          choice,
          taskResolutions: taskResolutions[conflictKey] || {},
        })
      );
      const res = await apiClient.post('/api/users/transfer-execute', {
        originalEmail: transferUser.email,
        targetEmail: selectedTransfer.targetUser.email,
        connectionId: selectedTransfer.connectionId,
        ownerEmail: user.email,
        resolutions: resolutionsList,
      });
      if (res.data.success) {
        deactivatedCount.current = res.data.deactivated ?? 0;
        const pendingRemoveUser = removeAfterTransfer ? transferUser : null;
        const pendingSuspendUser = suspendAfterTransfer ? transferUser : null;
        closeTransferModal();
        if (pendingRemoveUser) {
          setRemoveStep('executing');
          await handleRemoveExecute(pendingRemoveUser);
        } else if (pendingSuspendUser) {
          setSuspendStep('executing');
          await handleSuspendExecute(pendingSuspendUser);
        } else {
          const msg = deactivatedCount.current > 0
            ? `Team records transferred successfully. ${origName}'s transferred task lists are now inactive.`
            : 'Team records transferred successfully.';
          setTransferSuccessMsg(msg);
          setTimeout(() => setTransferSuccessMsg(null), 6000);
        }
      } else {
        setTransferError(res.data.message || 'Transfer failed');
      }
    } catch (err) {
      setTransferError(err.response?.data?.message || err.message || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const loadConnections = async () => {
    setAddUserConnectionsLoading(true);
    setAddUserConnectionsError(null);
    try {
      const res = await apiClient.get('/api/users/connections');
      if (res.data.success) {
        setAddUserConnections(res.data.connections || []);
      } else {
        setAddUserConnectionsError(res.data.message || 'Failed to load teams.');
      }
    } catch (err) {
      setAddUserConnectionsError(err.response?.data?.message || 'Failed to load teams.');
    } finally {
      setAddUserConnectionsLoading(false);
    }
  };

  const resetAddForm = () => {
    setAddUserRole('member');
    setAddUserEmail('');
    setAddUserFirstName('');
    setAddUserLastName('');
    setAddUserConnectionId('');
    setAddUserPermissions([]);
    setAddUserConnections([]);
    setAddUserConnectionsError(null);
    setAddUserError(null);
    setAddUserAttempted(false);
    setAddUserTouched({});
  };

  const openAddUser = () => {
    resetAddForm();
    loadConnections();
    setAddUserOpen(true);
  };

  const closeAddUser = () => {
    if (addUserSubmitting) return;
    setAddUserOpen(false);
    resetAddForm();
  };

  const ALL_ADMIN_PERMISSIONS = ['manage_billing', 'manage_users', 'task_reminders', 'calendar_connect', 'archive_tasks'];

  const handleAddUserRoleChange = (value) => {
    setAddUserRole(value);
    setAddUserPermissions(value === 'admin' ? ALL_ADMIN_PERMISSIONS : []);
  };

  const togglePermission = (perm) => {
    setAddUserPermissions(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
  };

  const isValidEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  const addUserErrors = {
    firstName: !addUserFirstName.trim(),
    lastName:  !addUserLastName.trim(),
    email:     !addUserEmail.trim() || !isValidEmail(addUserEmail),
    team:      addUserRole === 'member' && !addUserConnectionId,
  };
  const isAddUserFormValid = !Object.values(addUserErrors).some(Boolean);
  const touchField = (field) => setAddUserTouched(prev => ({ ...prev, [field]: true }));
  const addUserFieldBorder = (field) =>
    (addUserAttempted || addUserTouched[field]) && addUserErrors[field]
      ? '1.5px solid #dc2626'
      : '1px solid #d1d5db';

  const handleAddUser = async () => {
    setAddUserAttempted(true);
    setAddUserError(null);
    if (!isAddUserFormValid) return;
    setAddUserSubmitting(true);
    try {
      const res = await apiClient.post('/api/users/add', {
        role: addUserRole,
        email: addUserEmail.trim().toLowerCase(),
        firstName: addUserFirstName.trim(),
        lastName: addUserLastName.trim(),
        connectionId: addUserConnectionId || undefined,
        permissions: addUserRole === 'admin' ? addUserPermissions : [],
      });
      if (res.data.success) {
        closeAddUser();
        await loadUsers();
      } else {
        setAddUserError(res.data.message || 'Failed to add user.');
      }
    } catch (err) {
      setAddUserError(err.response?.data?.message || err.message || 'Failed to add user.');
    } finally {
      setAddUserSubmitting(false);
    }
  };

  const thStyle = (col) => ({
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: 13,
    color: '#374151',
    borderBottom: '2px solid #e5e7eb',
    background: '#f9fafb',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  });

  const tdStyle = {
    padding: '8px 16px',
    fontSize: 14,
    color: '#111827',
    borderBottom: '1px solid #f3f4f6',
    verticalAlign: 'middle',
  };

  const displayName = (u) => {
    const full = [u.name, u.last_name].filter(Boolean).join(' ');
    return full || u.email;
  };

  const targetDisplayName = (member) => {
    if (!member) return '';
    const full = [member.name, member.last_name].filter(Boolean).join(' ');
    return full || member.email;
  };

  const TASK_FIELD_LABELS = {
    title: 'Title', due: 'Due Date', action_items: 'Action Items',
    status: 'Status', description: 'Description', notes: 'Notes',
    deleted: 'Removed from List', active: 'Active',
  };

  const formatTaskFieldValue = (val) => {
    if (val === null || val === undefined) return <em style={{ color: '#9ca3af', fontStyle: 'italic' }}>empty</em>;
    if (Array.isArray(val)) {
      if (val.length === 0) return <em style={{ color: '#9ca3af', fontStyle: 'italic' }}>empty</em>;
      return (
        <ul style={{ margin: 0, paddingLeft: 14 }}>
          {val.map((item, i) => <li key={i}>{String(item)}</li>)}
        </ul>
      );
    }
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    return String(val);
  };

  const transferModalTitle = () => {
    if (transferStep === 'select') return 'Transfer Tasks';
    if (transferStep === 'checking') return 'Checking for Conflicts…';
    if (transferStep === 'no-records') return 'Nothing to Transfer';
    if (transferStep === 'confirm') return 'Confirm Transfer';
    if (transferStep === 'conflict-warning') return 'Review Conflicts Before Transfer';
    if (transferStep === 'conflict-review') return 'Review Transfer Conflicts';
    if (transferStep === 'final-confirm') return 'Confirm Transfer';
    return 'Transfer Tasks';
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <NavBar
        onProfileClick={onProfileClick}
        onBillingClick={onBillingClick}
        onSeatsUpdate={onSeatsUpdate}
      />

      <header className="App-header" style={{
        background: 'linear-gradient(180deg, #eef2f0 0%, #e4ece8 100%)',
        borderBottom: '1px solid #c8d8d2',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.07)',
        borderRadius: 0,
        margin: 0,
        padding: '0 20px',
        minHeight: 42,
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', position: 'relative' }}>
          {user && effectiveAccountType && (
            <div style={{
              position: 'absolute', left: 0,
              background: '#dcfce7', color: '#15803d',
              padding: '3px 10px', borderRadius: 999,
              fontSize: 12, fontWeight: 600,
              border: '1px solid #bbf7d0',
              letterSpacing: '0.01em',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span>
                {effectiveAccountType === 'Solo' ? 'Solo Account' :
                 effectiveAccountType === 'Solo_Assistant' ? 'Solo + Assistant' :
                 effectiveAccountType === 'Trial' ? '14-Day Free Trial' :
                 effectiveAccountType === 'Brokerage' ? 'Brokerage Account' :
                 effectiveAccountType === 'Super' ? 'Super Account' :
                 effectiveAccountType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
              <span style={{
                background: availableSeats === 0 ? '#fee2e2' : '#d1fae5',
                color: availableSeats === 0 ? '#dc2626' : '#15803d',
                borderRadius: 999, padding: '1px 7px', fontWeight: 600, fontSize: 12,
                border: `1px solid ${availableSeats === 0 ? '#fca5a5' : '#a7f3d0'}`,
              }}>
                {availableSeats} seat{availableSeats !== 1 ? 's' : ''} available
              </span>
              <span style={{ color: '#64748b', fontSize: 11 }}>(Max: {seatLimit})</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: '#d1fae5', color: '#047857',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14,
            }}><FaUserFriends /></div>
            <h2 style={{
              margin: 0, color: '#1e3a2f',
              fontSize: 18, fontWeight: 700,
              letterSpacing: '-0.01em',
            }}>Users</h2>
          </div>
          {hasDelegatedWorkspace && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Viewing:</span>
              <select
                value={isViewingDelegated ? 'delegated' : 'own'}
                onChange={(e) => setViewingDelegatedWorkspace(e.target.value === 'delegated')}
                style={{
                  fontSize: 13, padding: '4px 8px', borderRadius: 6,
                  border: '1px solid #cbd5e1', background: '#fff', color: '#1e3a2f',
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                <option value="own">My Workspace</option>
                <option value="delegated">
                  {(user?.admin_for_owner_name || user?.admin_for_owner_email || 'Delegated Workspace')} (Admin)
                </option>
              </select>
            </div>
          )}
          {(isOwner || canManageUsersTeams) && (
            <button
              onClick={openAddUser}
              style={{
                position: 'absolute', right: 0,
                background: 'linear-gradient(135deg, #059669, #047857)',
                border: 'none', borderRadius: 6, padding: '5px 14px',
                fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                boxShadow: '0 2px 4px rgba(5,150,105,0.3)',
                transition: 'all 0.2s ease-in-out',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #047857, #065f46)'; e.currentTarget.style.boxShadow = '0 4px 8px rgba(5,150,105,0.45)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #059669, #047857)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(5,150,105,0.3)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <FaPlus style={{ fontSize: 10 }} />
              Add User
            </button>
          )}
        </div>
      </header>

      <div style={{ maxWidth: 1500, margin: '0 auto', padding: '12px 24px 60px' }}>

        {/* Error banner */}
        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
            padding: '12px 16px', color: '#991b1b', fontSize: 14, marginBottom: 20,
          }}>
            {error}
          </div>
        )}

        {/* Table card */}
        <div style={{
          background: '#fff', borderRadius: 12,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden',
        }}>
          {loading ? (
            <div style={{ padding: '60px 24px', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
              Loading users…
            </div>
          ) : sortedUsers.length === 0 ? (
            <div style={{ padding: '72px 24px', textAlign: 'center' }}>
              <FaUser style={{ fontSize: 40, color: '#d1d5db', marginBottom: 14 }} />
              <p style={{ fontWeight: 600, fontSize: 16, color: '#374151', margin: '0 0 6px' }}>
                No users yet
              </p>
              <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>
                Invite team members from Teams to start collaborating.
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {COLS.map(col => (
                      <th key={col.key} style={thStyle(col.key)} onClick={() => handleSort(col.key)}>
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          {col.label}
                          <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                        </span>
                      </th>
                    ))}
                    <th style={{ ...thStyle('actions'), textAlign: 'center', cursor: 'default' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((u, i) => (
                    <tr
                      key={u.email}
                      style={{ background: u.suspended ? '#fdf6e3' : (i % 2 === 0 ? '#fff' : '#fafafa'), opacity: u.suspended ? 0.85 : 1 }}
                      onMouseEnter={e => { e.currentTarget.style.background = u.suspended ? '#fef3c7' : '#f0f4ff'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = u.suspended ? '#fdf6e3' : (i % 2 === 0 ? '#fff' : '#fafafa'); }}
                    >
                      {/* User */}
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <Avatar name={u.name} lastName={u.last_name} email={u.email} />
                          <span style={{ fontWeight: 600, color: '#111827' }}>{displayName(u)}</span>
                        </div>
                      </td>

                      {/* Email */}
                      <td style={{ ...tdStyle, color: '#4b5563' }}>
                        {u.email}
                      </td>

                      {/* Team */}
                      <td style={tdStyle}>
                        {u.teams && u.teams.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {u.teams.map(t => (
                              <span key={t} style={{
                                background: '#eff6ff', color: '#1d4ed8',
                                borderRadius: 10, padding: '2px 9px',
                                fontSize: 12, fontWeight: 500,
                              }}>
                                {t}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: 13 }}>—</span>
                        )}
                      </td>

                      {/* Role */}
                      <td style={{ ...tdStyle, color: '#374151' }}>
                        {roleLabel(u)}
                      </td>

                      {/* Status */}
                      <td style={tdStyle}>
                        {statusBadge(u.status)}
                      </td>

                      {/* Actions */}
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                        {u.suspended ? (
                          /* Suspended — only Reactivate */
                          <button
                            onClick={() => { setReactivateUser(u); setReactivateError(null); }}
                            style={{
                              background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
                              border: '1px solid #86efac',
                              borderRadius: 6, padding: '5px 12px', fontSize: 13,
                              color: '#166534', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: '0 2px 4px rgba(22,163,74,0.15)',
                              transform: 'translateY(0)', fontWeight: 500,
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #16a34a, #15803d)';
                              e.currentTarget.style.borderColor = '#15803d';
                              e.currentTarget.style.color = '#fff';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(22,163,74,0.35)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #dcfce7, #bbf7d0)';
                              e.currentTarget.style.borderColor = '#86efac';
                              e.currentTarget.style.color = '#166534';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(22,163,74,0.15)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                            onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px)'; }}
                            onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          >
                            <FaPlay style={{ fontSize: 10 }} />
                            Reactivate
                          </button>
                        ) : (
                          <>
                          {/* Impersonate */}
                          <button
                            onClick={() => { setConfirmImpersonateUser(u); setImpersonateError(null); }}
                            style={{
                              background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                              border: '1px solid #93c5fd',
                              borderRadius: 6, padding: '5px 12px', fontSize: 13,
                              color: '#1d4ed8', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: '0 2px 4px rgba(59,130,246,0.15)',
                              transform: 'translateY(0)', fontWeight: 500,
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6, #2563eb)';
                              e.currentTarget.style.borderColor = '#2563eb';
                              e.currentTarget.style.color = '#fff';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(59,130,246,0.35)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #eff6ff, #dbeafe)';
                              e.currentTarget.style.borderColor = '#93c5fd';
                              e.currentTarget.style.color = '#1d4ed8';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(59,130,246,0.15)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                            onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(59,130,246,0.2)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                            onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(59,130,246,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          >
                            <FaUserSecret style={{ fontSize: 11 }} />
                            Impersonate
                          </button>

                          {/* Transfer */}
                          <button
                            onClick={() => openTransferModal(u)}
                            style={{
                              background: 'linear-gradient(135deg, #faf5ff, #ede9fe)',
                              border: '1px solid #c4b5fd',
                              borderRadius: 6, padding: '5px 12px', fontSize: 13,
                              color: '#6d28d9', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: '0 2px 4px rgba(124,58,237,0.15)',
                              transform: 'translateY(0)', fontWeight: 500,
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #7c3aed, #6d28d9)';
                              e.currentTarget.style.borderColor = '#6d28d9';
                              e.currentTarget.style.color = '#fff';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(124,58,237,0.35)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #faf5ff, #ede9fe)';
                              e.currentTarget.style.borderColor = '#c4b5fd';
                              e.currentTarget.style.color = '#6d28d9';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(124,58,237,0.15)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                            onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(124,58,237,0.2)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                            onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(124,58,237,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          >
                            <FaArrowRight style={{ fontSize: 11 }} />
                            Transfer
                          </button>

                          {/* Suspend */}
                          <button
                            onClick={() => { setSuspendUser(u); setSuspendError(null); setSuspendStep('checking'); setSuspendCheckResult(null); setSuspendAfterTransfer(false); handleSuspendCheck(u); }}
                            style={{
                              background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                              border: '1px solid #fcd34d',
                              borderRadius: 6, padding: '5px 12px', fontSize: 13,
                              color: '#92400e', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: '0 2px 4px rgba(217,119,6,0.15)',
                              transform: 'translateY(0)', fontWeight: 500,
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #d97706, #b45309)';
                              e.currentTarget.style.borderColor = '#b45309';
                              e.currentTarget.style.color = '#fff';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(217,119,6,0.35)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #fffbeb, #fef3c7)';
                              e.currentTarget.style.borderColor = '#fcd34d';
                              e.currentTarget.style.color = '#92400e';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(217,119,6,0.15)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                            onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(217,119,6,0.2)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                            onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(217,119,6,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          >
                            <FaPause style={{ fontSize: 10 }} />
                            Suspend
                          </button>

                          {/* Remove */}
                          <button
                            onClick={() => { setConfirmUser(u); setRemoveError(null); setRemoveStep('checking'); setRemoveCheckResult(null); setRemoveAfterTransfer(false); handleRemoveCheck(u); }}
                            style={{
                              background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
                              border: '1px solid #fca5a5',
                              borderRadius: 6, padding: '5px 12px', fontSize: 13,
                              color: '#b91c1c', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: '0 2px 4px rgba(239,68,68,0.2)',
                              transform: 'translateY(0)', fontWeight: 500,
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444, #dc2626)';
                              e.currentTarget.style.borderColor = '#dc2626';
                              e.currentTarget.style.color = '#fff';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(239,68,68,0.4)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2, #fecaca)';
                              e.currentTarget.style.borderColor = '#fca5a5';
                              e.currentTarget.style.color = '#b91c1c';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(239,68,68,0.2)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                            onMouseDown={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(239,68,68,0.3)'; e.currentTarget.style.transform = 'translateY(1px)'; }}
                            onMouseUp={e => { e.currentTarget.style.boxShadow = '0 4px 8px rgba(239,68,68,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          >
                            <FaTrash style={{ fontSize: 11 }} />
                            Remove
                          </button>
                          </>
                        )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer count */}
        {!loading && sortedUsers.length > 0 && (
          <p style={{ marginTop: 12, fontSize: 13, color: '#9ca3af', textAlign: 'right' }}>
            {sortedUsers.length} user{sortedUsers.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      <style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
      {/* Remove Member Modal — multi-step */}
      {confirmUser && !transferUser && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div
        className={`modal ${(confirmUser && !transferUser) ? 'show' : ''}`}
        style={{ display: (confirmUser && !transferUser) ? 'block' : 'none' }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px', width: '90%' }}>
          <div className="modal-content">

            {/* ── Header ── */}
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">
                {removeStep === 'need_transfer' ? 'Transfer Required Before Removal'
                  : removeStep === 'blocked'       ? 'Lead Agent Replacement Required'
                  : removeStep === 'checking'      ? 'Checking…'
                  : removeStep === 'executing'     ? 'Removing User…'
                  : 'Remove User?'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeRemoveModal}
                disabled={removing || removeStep === 'checking'}
                aria-label="Close"
                style={{ filter: 'invert(1)' }}
              />
            </div>

            {/* ── Body ── */}
            <div className="modal-body p-4" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif' }}>

              {/* User identity card — shown on all steps */}
              {confirmUser && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '10px 12px', background: '#f8f9fa', borderRadius: 6, border: '1px solid #e9ecef' }}>
                  <Avatar name={confirmUser.name} lastName={confirmUser.last_name} email={confirmUser.email} size={38} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#212529' }}>{displayName(confirmUser)}</div>
                    <div style={{ fontSize: 13, color: '#6c757d' }}>{confirmUser.email}</div>
                  </div>
                </div>
              )}

              {/* Step: checking / executing */}
              {(removeStep === 'checking' || removeStep === 'executing') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                  <div style={{ width: 20, height: 20, border: '3px solid #e9ecef', borderTop: '3px solid #dc3545', borderRadius: '50%', animation: 'spin 0.9s linear infinite', flexShrink: 0 }} />
                  <span style={{ fontSize: 14, color: '#374151' }}>
                    {removeStep === 'checking' ? 'Checking member status…' : 'Removing member…'}
                  </span>
                </div>
              )}

              {/* Step: ready */}
              {removeStep === 'ready' && removeCheckResult && (
                <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
                  {removeCheckResult.delete_scope === 'team_scoped_delete_only'
                    ? 'This user has a paid or trial account, or belongs to other workspaces. Removing them will only remove them from your team — their account and other workspace data will not be deleted.'
                    : 'This user has no active task lists. Removing them will permanently remove their access and records from this workspace.'}
                </p>
              )}

              {/* Step: need_transfer */}
              {removeStep === 'need_transfer' && removeCheckResult && (
                <>
                  <p style={{ margin: '0 0 12px', fontSize: 14 }}>
                    This user has active task lists. Transfer them to another team member before removing this user.
                  </p>
                  <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: '10px 14px', marginBottom: 4, fontSize: 13, display: 'flex', gap: 20 }}>
                    <span><span style={{ fontWeight: 600, color: '#92400e' }}>Active task lists: </span><span style={{ color: '#78350f' }}>{removeCheckResult.active_task_list_count}</span></span>
                    {removeCheckResult.affected_team_count != null && (
                      <span><span style={{ fontWeight: 600, color: '#92400e' }}>Teams affected: </span><span style={{ color: '#78350f' }}>{removeCheckResult.affected_team_count}</span></span>
                    )}
                  </div>
                </>
              )}

              {/* Step: blocked */}
              {removeStep === 'blocked' && (
                <p style={{ margin: 0, fontSize: 14, color: '#374151' }}>
                  <strong>{displayName(confirmUser)}</strong> is the Lead Agent for one or more teams.
                  Please assign a new Lead Agent before removing this user.
                </p>
              )}

              {removeError && (
                <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: 13 }}>{removeError}</div>
              )}
            </div>

            {/* ── Footer ── */}
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              {(removeStep === 'ready' || removeStep === 'need_transfer' || removeStep === 'blocked') && (
                <Button variant="secondary" onClick={closeRemoveModal} disabled={removing} style={cancelBtnStyle}>
                  Cancel
                </Button>
              )}
              {removeStep === 'ready' && (
                <Button
                  variant="danger"
                  onClick={() => handleRemoveExecute()}
                  disabled={removing}
                  style={primaryBtnStyle}
                >
                  {removing ? 'Removing…' : 'Remove User'}
                </Button>
              )}
              {removeStep === 'need_transfer' && (
                <Button
                  onClick={() => {
                    setRemoveAfterTransfer(true);
                    openTransferModal(confirmUser);
                  }}
                  disabled={removing}
                  style={{ ...primaryBtnStyle, background: '#f97316', borderColor: '#ea580c', color: '#fff' }}
                >
                  Transfer &amp; Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Suspend Modal ── */}
      {suspendUser && !transferUser && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div
        className={`modal ${(suspendUser && !transferUser) ? 'show' : ''}`}
        style={{ display: (suspendUser && !transferUser) ? 'block' : 'none' }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">
                {suspendStep === 'need_transfer' ? 'Transfer Required Before Suspension'
                  : suspendStep === 'blocked' ? 'Lead Agent Replacement Required'
                  : suspendStep === 'checking' ? 'Checking…'
                  : 'Suspend User?'}
              </h5>
              <button type="button" className="btn-close" onClick={closeSuspendModal} disabled={suspending || suspendStep === 'checking'} aria-label="Close" style={{ filter: 'invert(1)' }} />
            </div>

            <div className="modal-body p-4" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif' }}>
              {suspendUser && suspendStep !== 'checking' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '10px 12px', background: '#f8f9fa', borderRadius: 6, border: '1px solid #e9ecef' }}>
                  <Avatar name={suspendUser.name} lastName={suspendUser.last_name} email={suspendUser.email} size={38} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#212529' }}>{displayName(suspendUser)}</div>
                    <div style={{ fontSize: 13, color: '#6c757d' }}>{suspendUser.email}</div>
                  </div>
                </div>
              )}

              {(suspendStep === 'checking' || suspendStep === 'executing') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                  <div style={{ width: 20, height: 20, border: '3px solid #e9ecef', borderTop: '3px solid #d97706', borderRadius: '50%', animation: 'spin 0.9s linear infinite', flexShrink: 0 }} />
                  <span style={{ fontSize: 14, color: '#374151' }}>
                    {suspendStep === 'checking' ? 'Checking active task lists…' : 'Suspending member…'}
                  </span>
                </div>
              )}

              {suspendStep === 'ready' && (
                <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
                  This user has no active task lists. Suspending them will pause their team access, notifications, reminders, and calendar sync. They can be reactivated later.
                </p>
              )}

              {suspendStep === 'need_transfer' && (
                <>
                  <p style={{ margin: '0 0 12px', fontSize: 14, color: '#374151' }}>
                    This user has active task lists. To avoid leaving work assigned to a suspended user, transfer their task lists to another team member before suspension.
                  </p>
                  {suspendCheckResult && (
                    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '10px 14px', marginBottom: 4, fontSize: 13, display: 'flex', gap: 20 }}>
                      <span><span style={{ fontWeight: 600, color: '#92400e' }}>Active task lists: </span><span style={{ color: '#78350f' }}>{suspendCheckResult.active_task_list_count}</span></span>
                      {suspendCheckResult.affected_team_count != null && (
                        <span><span style={{ fontWeight: 600, color: '#92400e' }}>Teams affected: </span><span style={{ color: '#78350f' }}>{suspendCheckResult.affected_team_count}</span></span>
                      )}
                    </div>
                  )}
                </>
              )}

              {suspendStep === 'blocked' && (
                <p style={{ margin: 0, fontSize: 14, color: '#374151' }}>
                  <strong>{displayName(suspendUser)}</strong> is the Lead Agent for one or more teams.
                  Please assign a new Lead Agent before suspending this user.
                </p>
              )}

              {suspendError && (
                <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: 13 }}>{suspendError}</div>
              )}
            </div>

            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              {suspendStep !== 'checking' && suspendStep !== 'executing' && (
                <Button variant="secondary" onClick={closeSuspendModal} disabled={suspending} style={cancelBtnStyle}>Cancel</Button>
              )}
              {suspendStep === 'ready' && (
                <Button
                  onClick={() => handleSuspendExecute()}
                  disabled={suspending}
                  style={{ ...primaryBtnStyle, background: '#d97706', borderColor: '#b45309', color: '#fff' }}
                >
                  {suspending ? 'Suspending…' : 'Suspend User'}
                </Button>
              )}
              {suspendStep === 'need_transfer' && (
                <Button
                  onClick={() => { setSuspendAfterTransfer(true); openTransferModal(suspendUser); }}
                  disabled={suspending}
                  style={{ ...primaryBtnStyle, background: '#f97316', borderColor: '#ea580c', color: '#fff' }}
                >
                  Transfer &amp; Suspend
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Reactivate Modal ── */}
      {reactivateUser && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div
        className={`modal ${reactivateUser ? 'show' : ''}`}
        style={{ display: reactivateUser ? 'block' : 'none' }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">Reactivate User?</h5>
              <button type="button" className="btn-close" onClick={() => { if (!reactivating) { setReactivateUser(null); setReactivateError(null); } }} disabled={reactivating} aria-label="Close" style={{ filter: 'invert(1)' }} />
            </div>

            <div className="modal-body p-4" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif' }}>
              {reactivateUser && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '10px 12px', background: '#f8f9fa', borderRadius: 6, border: '1px solid #e9ecef' }}>
                  <Avatar name={reactivateUser.name} lastName={reactivateUser.last_name} email={reactivateUser.email} size={38} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#212529' }}>{displayName(reactivateUser)}</div>
                    <div style={{ fontSize: 13, color: '#6c757d' }}>{reactivateUser.email}</div>
                  </div>
                </div>
              )}
              <p style={{ margin: '0 0 8px' }}>
                This will reactivate the user on this team and restore their team access if there is an available seat.
              </p>
              {reactivateError && (
                <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: 13 }}>{reactivateError}</div>
              )}
            </div>

            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <Button variant="secondary" onClick={() => { if (!reactivating) { setReactivateUser(null); setReactivateError(null); } }} disabled={reactivating} style={cancelBtnStyle}>Cancel</Button>
              <Button
                onClick={handleReactivate}
                disabled={reactivating}
                style={{ ...primaryBtnStyle, background: '#16a34a', borderColor: '#15803d', color: '#fff' }}
              >
                {reactivating ? 'Reactivating…' : 'Reactivate'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Impersonate Confirmation Modal */}
      {confirmImpersonateUser && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div
        className={`modal ${confirmImpersonateUser ? 'show' : ''}`}
        style={{ display: confirmImpersonateUser ? 'block' : 'none' }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">
                Switch to {confirmImpersonateUser ? displayName(confirmImpersonateUser) : ''}?
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => { if (!impersonating) setConfirmImpersonateUser(null); }}
                aria-label="Close"
                style={{ filter: 'invert(1)' }}
              />
            </div>

            <div className="modal-body p-4" style={{
              background: '#fff',
              fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif',
            }}>
              {confirmImpersonateUser && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  marginBottom: 16, padding: '10px 12px',
                  background: '#f8f9fa', borderRadius: 6,
                  border: '1px solid #e9ecef',
                }}>
                  <Avatar
                    name={confirmImpersonateUser.name}
                    lastName={confirmImpersonateUser.last_name}
                    email={confirmImpersonateUser.email}
                    size={38}
                  />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#212529' }}>
                      {displayName(confirmImpersonateUser)}
                    </div>
                    <div style={{ fontSize: 13, color: '#6c757d' }}>{confirmImpersonateUser.email}</div>
                  </div>
                </div>
              )}
              <p style={{ margin: '0 0 6px' }}>
                You'll temporarily view SmartClose as <strong>{confirmImpersonateUser ? displayName(confirmImpersonateUser) : ''}</strong>. You can return to your account at any time.
              </p>
              {impersonateError && (
                <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: 13 }}>
                  {impersonateError}
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <Button
                variant="secondary"
                onClick={() => setConfirmImpersonateUser(null)}
                disabled={impersonating}
                style={cancelBtnStyle}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleImpersonateConfirmed}
                disabled={impersonating}
                style={primaryBtnStyle}
              >
                {impersonating ? 'Switching…' : 'Switch User'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Transfer Modal */}
      {transferUser && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div
        className={`modal ${transferUser ? 'show' : ''}`}
        style={{ display: transferUser ? 'block' : 'none' }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{
          maxWidth: transferStep === 'conflict-review'
            ? `min(${transferConflicts.some(c => c.conflictingTasks?.length > 0) ? 900 : 660}px, 92vw)`
            : '560px',
        }}>
          <div className="modal-content">

            {/* Header */}
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">{transferModalTitle()}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeTransferModal}
                aria-label="Close"
                style={{ filter: 'invert(1)' }}
                disabled={transferring || transferStep === 'checking'}
              />
            </div>

            {/* Body */}
            <div className="modal-body p-4" style={{
              background: '#fff',
              fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif',
            }}>

              {/* ── Step: select ── */}
              {transferStep === 'select' && transferUser && (
                <>
                  {/* From user */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', background: '#f8f9fa',
                    borderRadius: 6, border: '1px solid #e9ecef', marginBottom: 14,
                  }}>
                    <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, minWidth: 80 }}>
                      Transfer from
                    </div>
                    <Avatar name={transferUser.name} lastName={transferUser.last_name} email={transferUser.email} size={30} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{displayName(transferUser)}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{transferUser.email}</div>
                    </div>
                  </div>

                  <p style={{ margin: '0 0 14px', fontSize: 14, color: '#374151' }}>
                    Choose who should receive this user's team task and calendar records.
                  </p>

                  {transferTargetsLoading && (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#6b7280', fontSize: 14 }}>
                      <div className="spinner-border text-secondary mb-2" role="status"
                        style={{ width: 22, height: 22, borderWidth: 2, display: 'block', margin: '0 auto 8px' }}>
                        <span className="visually-hidden">Loading…</span>
                      </div>
                      Loading eligible users…
                    </div>
                  )}

                  {transferTargetsError && (
                    <div className="alert alert-danger" style={{ fontSize: 13 }}>
                      {transferTargetsError}
                    </div>
                  )}

                  {!transferTargetsLoading && !transferTargetsError && transferTargets.length === 0 && (
                    <div style={{
                      textAlign: 'center', padding: '20px 0',
                      color: '#6b7280', fontSize: 14,
                    }}>
                      No eligible users found on shared teams.
                    </div>
                  )}

                  {!transferTargetsLoading && transferTargets.map(team => (
                    <div key={team.connectionId} style={{ marginBottom: 16 }}>
                      <div style={{
                        fontSize: 11, fontWeight: 700, color: '#6b7280',
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                        marginBottom: 8, paddingBottom: 4,
                        borderBottom: '1px solid #f3f4f6',
                      }}>
                        {team.teamName}
                      </div>
                      {team.members.map(member => {
                        const isSelected = selectedTransfer?.targetUser.email === member.email
                          && selectedTransfer?.connectionId === team.connectionId;
                        return (
                          <label
                            key={member.email}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                              border: `1px solid ${isSelected ? '#c4b5fd' : '#e9ecef'}`,
                              background: isSelected ? '#faf5ff' : '#fff',
                              marginBottom: 6, transition: 'all 0.12s',
                            }}
                          >
                            <input
                              type="radio"
                              name="transfer-target"
                              checked={isSelected}
                              onChange={() => setSelectedTransfer({
                                targetUser: member,
                                connectionId: team.connectionId,
                                teamName: team.teamName,
                              })}
                              style={{ accentColor: '#7c3aed', flexShrink: 0 }}
                            />
                            <Avatar name={member.name} lastName={member.last_name} email={member.email} size={28} />
                            <div style={{ flex: 1 }}>
                              <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
                                {targetDisplayName(member)}
                              </span>
                              <span style={{ color: '#6b7280', fontSize: 13 }}>
                                {' — '}{member.role_label || 'Member'}
                              </span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ))}

                  {transferError && (
                    <div className="alert alert-danger mt-2 mb-0" style={{ fontSize: 13 }}>
                      {transferError}
                    </div>
                  )}
                </>
              )}

              {/* ── Step: checking ── */}
              {transferStep === 'checking' && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div className="spinner-border mb-3" role="status"
                    style={{ width: 32, height: 32, borderWidth: 3, color: '#7c3aed', display: 'block', margin: '0 auto 12px' }}>
                    <span className="visually-hidden">Checking…</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 14, color: '#374151' }}>
                    Checking records for{' '}
                    <strong>{targetDisplayName(selectedTransfer?.targetUser)}</strong>
                    {' '}on{' '}
                    <strong>{selectedTransfer?.teamName}</strong>…
                  </p>
                </div>
              )}

              {/* ── Step: no-records ── */}
              {transferStep === 'no-records' && (
                <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: '#f3f4f6', color: '#9ca3af',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 26, margin: '0 auto 16px', fontWeight: 300,
                  }}>&#8709;</div>
                  <p style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600, color: '#111827' }}>
                    Nothing to Transfer
                  </p>
                  <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
                    This user does not have any active task lists to transfer in your workspace.
                  </p>
                </div>
              )}

              {/* ── Step: confirm (no conflicts) ── */}
              {transferStep === 'confirm' && (
                <>
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: '#dcfce7', color: '#16a34a',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, margin: '0 auto',
                    }}>
                      &#10003;
                    </div>
                  </div>
                  <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
                    This will transfer tasks from{' '}
                    <strong>{displayName(transferUser)}</strong> to{' '}
                    <strong>{targetDisplayName(selectedTransfer?.targetUser)}</strong>{' '}
                    for <strong>{selectedTransfer?.teamName}</strong>.
                  </p>
                  {transferError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: 13 }}>
                      {transferError}
                    </div>
                  )}
                </>
              )}

              {/* ── Step: conflict-warning ── */}
              {transferStep === 'conflict-warning' && (
                <>
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '14px 16px', background: '#fff7ed',
                    borderRadius: 8, border: '1px solid #fed7aa', marginBottom: 14,
                  }}>
                    {/* Icon */}
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: '#ea580c', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, fontWeight: 800, flexShrink: 0, lineHeight: 1,
                    }}>!</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: '#9a3412' }}>
                          Conflicts Require Review
                        </span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          minWidth: 22, height: 22, padding: '0 7px',
                          background: '#ea580c', color: '#fff',
                          borderRadius: 11, fontSize: 12, fontWeight: 700, lineHeight: 1,
                        }}>
                          {transferConflicts.length}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: '#7c2d12', lineHeight: 1.5 }}>
                        {transferConflicts.some(c => c.conflictType === 'task_content_differs')
                          ? 'Both users have changes on the same task list. Please review which version to keep before completing the transfer.'
                          : `The selected user already has records or unpublished changes for ${transferConflicts.length} matching task list${transferConflicts.length !== 1 ? 's' : ''}. Please review how these records should be handled before completing the transfer.`
                        }
                      </div>
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: '#374151' }}>
                    Click <strong>Review Conflicts</strong> to choose how to handle each overlapping record,
                    or go <strong>Back</strong> to select a different user.
                  </p>
                </>
              )}

              {/* ── Step: conflict-review ── */}
              {transferStep === 'conflict-review' && (
                <>
                  <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>
                    Review each conflict below. Defaults are set to the safest option.
                  </p>
                  <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: 2 }}>
                    {transferConflicts.map((conflict, idx) => (
                      <div key={conflict.conflictKey} style={{
                        border: '1px solid #e5e7eb', borderRadius: 8,
                        padding: '12px 14px', background: '#fafafa',
                        marginBottom: idx < transferConflicts.length - 1 ? 12 : 0,
                      }}>
                        {/* Location */}
                        <div style={{ marginBottom: 10 }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '0.04em', color: '#6d28d9',
                            background: '#faf5ff', padding: '2px 8px',
                            borderRadius: 4, border: '1px solid #ede9fe',
                          }}>
                            {conflict.teamName}
                          </span>
                          {conflict.propertyAddress && (
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginTop: 6 }}>
                              {conflict.propertyAddress}
                            </div>
                          )}
                          {conflict.taskListTitle && (
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                              Task List: {conflict.taskListTitle}
                            </div>
                          )}
                        </div>

                        {/* Conflict description */}
                        <div style={{
                          fontSize: 13, color: '#92400e', background: '#fffbeb',
                          padding: '6px 10px', borderRadius: 6,
                          border: '1px solid #fde68a', marginBottom: 12,
                        }}>
                          {conflict.description}
                        </div>

                        {/* Task-level diff detail */}
                        {conflict.conflictingTasks?.length > 0 && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                              Conflicting Tasks ({conflict.conflictingTasks.length})
                            </div>
                            {conflict.conflictingTasks.map((task, ti) => {
                              const isMerging = conflictResolutions[conflict.conflictKey] === 'merge_tasks';
                              const taskKey = task.taskId || `task_${ti}`;
                              const taskChoice = (taskResolutions[conflict.conflictKey] || {})[taskKey];
                              return (
                                <div key={taskKey} style={{
                                  border: '1px solid #e5e7eb', borderRadius: 6,
                                  background: '#fff', marginBottom: 6, overflow: 'hidden',
                                }}>
                                  <div style={{
                                    padding: '5px 10px', background: '#f3f4f6',
                                    fontSize: 12, fontWeight: 600, color: '#111827',
                                    borderBottom: '1px solid #e5e7eb',
                                  }}>
                                    {task.title || `Task ${ti + 1}`}
                                  </div>
                                  <div style={{ padding: '8px 10px' }}>
                                    {task.diffFields.map(field => (
                                      <div key={field} style={{ marginBottom: 6 }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                                          {TASK_FIELD_LABELS[field] || field}
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                          <div style={{
                                            background: '#fef2f2', border: '1px solid #fecaca',
                                            borderRadius: 4, padding: '4px 8px', fontSize: 11,
                                          }}>
                                            <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: 2 }}>
                                              {displayName(transferUser)}
                                            </div>
                                            <div style={{ color: '#374151' }}>
                                              {formatTaskFieldValue(task.originalValues[field])}
                                            </div>
                                          </div>
                                          <div style={{
                                            background: '#f0fdf4', border: '1px solid #bbf7d0',
                                            borderRadius: 4, padding: '4px 8px', fontSize: 11,
                                          }}>
                                            <div style={{ fontWeight: 600, color: '#166534', marginBottom: 2 }}>
                                              {targetDisplayName(selectedTransfer?.targetUser)}
                                            </div>
                                            <div style={{ color: '#374151' }}>
                                              {formatTaskFieldValue(task.targetValues[field])}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    {/* Per-task resolution controls — visible only when "Merge task-by-task" is selected */}
                                    {isMerging && (
                                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                                          Keep which version of this task?
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                          {[
                                            { value: 'keep_original', label: `${displayName(transferUser)}'s` },
                                            { value: 'keep_target', label: `${targetDisplayName(selectedTransfer?.targetUser)}'s` },
                                            { value: undefined, label: 'Use newer' },
                                          ].map(opt => {
                                            const isTaskChecked = taskChoice === opt.value || (!taskChoice && opt.value === undefined);
                                            return (
                                              <label key={opt.value ?? 'newer'} style={{
                                                display: 'flex', alignItems: 'center', gap: 4,
                                                fontSize: 11, cursor: 'pointer',
                                                padding: '3px 8px', borderRadius: 4,
                                                background: isTaskChecked ? '#ede9fe' : '#f3f4f6',
                                                border: `1px solid ${isTaskChecked ? '#c4b5fd' : '#e5e7eb'}`,
                                                color: isTaskChecked ? '#5b21b6' : '#374151',
                                                fontWeight: isTaskChecked ? 600 : 400,
                                              }}>
                                                <input
                                                  type="radio"
                                                  name={`task_res_${conflict.conflictKey}_${taskKey}`}
                                                  checked={isTaskChecked}
                                                  onChange={() => setTaskResolutions(prev => ({
                                                    ...prev,
                                                    [conflict.conflictKey]: {
                                                      ...(prev[conflict.conflictKey] || {}),
                                                      [taskKey]: opt.value,
                                                    },
                                                  }))}
                                                  style={{ accentColor: '#7c3aed', flexShrink: 0 }}
                                                />
                                                {opt.label}
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Resolution choices */}
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                          How should this be handled?
                        </div>
                        {[
                          {
                            value: 'keep_target',
                            label: `Keep ${targetDisplayName(selectedTransfer?.targetUser)}'s version`,
                            sub: 'Target user\'s current work is preserved',
                            recommended: conflict.requiresReview,
                          },
                          {
                            value: 'keep_original',
                            label: `Keep ${displayName(transferUser)}'s version`,
                            sub: 'Overwrites the target user\'s current record',
                          },
                          {
                            value: 'keep_newer',
                            label: 'Keep the newer version',
                            sub: 'Based on last update timestamp',
                          },
                          ...(conflict.conflictingTasks?.length > 0 ? [{
                            value: 'merge_tasks',
                            label: 'Merge task-by-task',
                            sub: 'Choose which version to keep for each conflicting task',
                          }] : []),
                          {
                            value: 'skip',
                            label: 'Skip this item',
                            sub: 'Leave both records unchanged',
                          },
                        ].map(opt => {
                          const isChecked = conflictResolutions[conflict.conflictKey] === opt.value;
                          return (
                            <label key={opt.value} style={{
                              display: 'flex', alignItems: 'flex-start', gap: 8,
                              padding: '6px 8px', borderRadius: 5, cursor: 'pointer',
                              background: isChecked ? '#faf5ff' : 'transparent',
                              border: `1px solid ${isChecked ? '#c4b5fd' : 'transparent'}`,
                              marginBottom: 4,
                            }}>
                              <input
                                type="radio"
                                name={`resolution_${conflict.conflictKey}`}
                                checked={isChecked}
                                onChange={() => setConflictResolutions(prev => ({
                                  ...prev,
                                  [conflict.conflictKey]: opt.value,
                                }))}
                                style={{ accentColor: '#7c3aed', marginTop: 3, flexShrink: 0 }}
                              />
                              <div>
                                <div style={{ fontSize: 13, fontWeight: isChecked ? 600 : 400, color: '#111827' }}>
                                  {opt.label}
                                  {opt.recommended && (
                                    <span style={{
                                      marginLeft: 6, fontSize: 10, fontWeight: 700,
                                      color: '#7c3aed', background: '#ede9fe',
                                      padding: '1px 6px', borderRadius: 4,
                                      textTransform: 'uppercase', letterSpacing: '0.04em',
                                    }}>
                                      Recommended
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: 11, color: '#9ca3af' }}>{opt.sub}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ── Step: final-confirm ── */}
              {transferStep === 'final-confirm' && (
                <>
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: '#ede9fe', color: '#7c3aed',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 18, margin: '0 auto',
                    }}>
                      <FaArrowRight />
                    </div>
                  </div>
                  <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
                    This will transfer selected team records from{' '}
                    <strong>{displayName(transferUser)}</strong> to{' '}
                    <strong>{targetDisplayName(selectedTransfer?.targetUser)}</strong>{' '}
                    for <strong>{selectedTransfer?.teamName}</strong>.
                    Conflict choices will be applied as reviewed.
                  </p>
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: '#92400e', background: '#fffbeb', padding: '6px 10px', borderRadius: 6, border: '1px solid #fde68a' }}>
                    After transfer, <strong>{displayName(transferUser)}</strong> will no longer see these transferred task lists.
                  </p>
                  {transferConflicts.length > 0 && (
                    <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                      {transferConflicts.length} conflict{transferConflicts.length !== 1 ? 's' : ''} will be resolved using your selected options.
                    </p>
                  )}
                  {transferError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: 13 }}>
                      {transferError}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>

              {transferStep === 'select' && (
                <>
                  <Button variant="secondary" onClick={closeTransferModal} style={cancelBtnStyle}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleTransferContinue}
                    disabled={!selectedTransfer || transferTargetsLoading}
                    style={purplePrimaryStyle}
                  >
                    Continue
                  </Button>
                </>
              )}

              {transferStep === 'checking' && (
                <Button variant="secondary" disabled style={cancelBtnStyle}>
                  Cancel
                </Button>
              )}

              {transferStep === 'no-records' && (
                <Button variant="secondary" onClick={closeTransferModal} style={cancelBtnStyle}>
                  Close
                </Button>
              )}

              {transferStep === 'confirm' && (
                <>
                  <Button variant="secondary" onClick={() => setTransferStep('select')} disabled={transferring} style={cancelBtnStyle}>
                    Back
                  </Button>
                  <Button
                    onClick={handleTransferExecute}
                    disabled={transferring}
                    style={purplePrimaryStyle}
                  >
                    {transferring ? 'Transferring…' : 'Confirm Transfer'}
                  </Button>
                </>
              )}

              {transferStep === 'conflict-warning' && (
                <>
                  <Button variant="secondary" onClick={() => setTransferStep('select')} style={cancelBtnStyle}>
                    Back
                  </Button>
                  <Button
                    onClick={() => setTransferStep('conflict-review')}
                    style={{
                      background: '#d97706', borderColor: '#d97706', color: '#fff',
                      ...primaryBtnStyle,
                    }}
                  >
                    Review Conflicts
                  </Button>
                </>
              )}

              {transferStep === 'conflict-review' && (
                <>
                  <Button variant="secondary" onClick={() => setTransferStep('conflict-warning')} style={cancelBtnStyle}>
                    Back
                  </Button>
                  <Button
                    onClick={() => setTransferStep('final-confirm')}
                    style={purplePrimaryStyle}
                  >
                    Continue
                  </Button>
                </>
              )}

              {transferStep === 'final-confirm' && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => setTransferStep(transferConflicts.length > 0 ? 'conflict-review' : 'confirm')}
                    disabled={transferring}
                    style={cancelBtnStyle}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleTransferExecute}
                    disabled={transferring}
                    style={purplePrimaryStyle}
                  >
                    {transferring ? 'Transferring…' : 'Confirm Transfer'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {addUserOpen && <div className="modal-backdrop show" style={{ display: 'block' }} />}
      <div className={`modal ${addUserOpen ? 'show' : ''}`} style={{ display: addUserOpen ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520, width: '90%' }}>
          <div className="modal-content">

            <div className="modal-header" style={{ color: '#fff', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">Add User</h5>
              <button type="button" className="btn-close" onClick={closeAddUser} disabled={addUserSubmitting} aria-label="Close" style={{ filter: 'invert(1)' }} />
            </div>

            <div className="modal-body p-4" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif' }}>

              {/* Role selector */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { value: 'member', label: 'Member', desc: 'Team-level user, occupies a seat' },
                    { value: 'admin',  label: 'Admin',  desc: `Account-level, no seat (${usedAdmins}/${adminLimit} used)` },
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => { if (!addUserSubmitting) handleAddUserRoleChange(opt.value); }}
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', userSelect: 'none',
                        border: `1.5px solid ${addUserRole === opt.value ? '#059669' : '#e5e7eb'}`,
                        background: addUserRole === opt.value ? '#f0fdf4' : '#fff',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="radio" name="add-user-role" value={opt.value}
                          checked={addUserRole === opt.value}
                          onChange={() => handleAddUserRoleChange(opt.value)}
                          onClick={e => e.stopPropagation()}
                          style={{ accentColor: '#059669', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{opt.label}</div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{opt.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {addUserRole === 'admin' && adminLimit === 0 && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px' }}>
                    Your plan does not support admin users. Upgrade to add admins.
                  </div>
                )}
                {addUserRole === 'admin' && adminLimit > 0 && usedAdmins >= adminLimit && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px' }}>
                    Admin limit reached ({adminLimit}). Upgrade your plan for more admins.
                  </div>
                )}
              </div>

              {/* Name row */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <input
                    type="text" value={addUserFirstName}
                    onChange={e => setAddUserFirstName(e.target.value)}
                    onBlur={() => touchField('firstName')}
                    placeholder="First name"
                    style={{ width: '100%', padding: '7px 10px', border: addUserFieldBorder('firstName'), borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <input
                    type="text" value={addUserLastName}
                    onChange={e => setAddUserLastName(e.target.value)}
                    onBlur={() => touchField('lastName')}
                    placeholder="Last name"
                    style={{ width: '100%', padding: '7px 10px', border: addUserFieldBorder('lastName'), borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Email */}
              <div style={{ marginBottom: 12 }}>
                <input
                  type="email" value={addUserEmail}
                  onChange={e => setAddUserEmail(e.target.value)}
                  onBlur={() => touchField('email')}
                  placeholder="Email address"
                  style={{ width: '100%', padding: '7px 10px', border: addUserFieldBorder('email'), borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {/* Team selector — member only */}
              {addUserRole === 'member' && (
                <div style={{ marginBottom: 12 }}>
                  {addUserConnectionsLoading ? (
                    <div style={{ fontSize: 13, color: '#6b7280', padding: '6px 0' }}>Loading teams…</div>
                  ) : addUserConnectionsError ? (
                    <div style={{ fontSize: 13, color: '#b91c1c', padding: '6px 0' }}>
                      {addUserConnectionsError}{' '}
                      <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={loadConnections}>Retry</span>
                    </div>
                  ) : addUserConnections.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#9ca3af', padding: '6px 0' }}>No teams found. Create a team first in Edit Connections.</div>
                  ) : (
                    <select
                      value={addUserConnectionId}
                      onChange={e => setAddUserConnectionId(e.target.value)}
                      onBlur={() => touchField('team')}
                      style={{ width: '100%', padding: '7px 10px', border: addUserFieldBorder('team'), borderRadius: 6, fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box' }}
                    >
                      <option value="">Select team</option>
                      {addUserConnections.map(c => (
                        <option key={c.connection_id} value={c.connection_id}>
                          {c.name} ({c.active_member_count} active)
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Permissions — admin only */}
              {addUserRole === 'admin' && (
                <div style={{ marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Permissions</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[
                      { key: 'manage_billing',  label: 'Manage billing, seats & slots' },
                      { key: 'manage_users',    label: 'Manage users & teams' },
                      { key: 'task_reminders',  label: 'Receive due task reminder emails' },
                      { key: 'calendar_connect', label: 'Connect personal calendar' },
                      { key: 'archive_tasks',   label: 'Delete & archive task lists' },
                    ].map(p => (
                      <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
                        <input
                          type="checkbox"
                          checked={addUserPermissions.includes(p.key)}
                          onChange={() => togglePermission(p.key)}
                          style={{ accentColor: '#059669', width: 14, height: 14, flexShrink: 0 }}
                        />
                        {p.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {addUserError && (
                <div style={{ marginTop: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#991b1b' }}>
                  {addUserError}
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <Button variant="secondary" onClick={closeAddUser} disabled={addUserSubmitting} style={cancelBtnStyle}>Cancel</Button>
              <Button
                onClick={handleAddUser}
                disabled={addUserSubmitting || !isAddUserFormValid || (addUserRole === 'admin' && adminLimit === 0) || (addUserRole === 'admin' && usedAdmins >= adminLimit && adminLimit > 0)}
                style={{ ...primaryBtnStyle, background: '#059669', borderColor: '#047857', color: '#fff' }}
              >
                {addUserSubmitting ? 'Sending…' : 'Send Invite'}
              </Button>
            </div>

          </div>
        </div>
      </div>

      {/* Success toast */}
      {transferSuccessMsg && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: '#166534', color: '#fff', borderRadius: 8,
          padding: '12px 20px', fontSize: 14, fontWeight: 500,
          boxShadow: '0 4px 14px rgba(0,0,0,0.22)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontSize: 16 }}>&#10003;</span>
          {transferSuccessMsg}
        </div>
      )}
    </div>
  );
}
